import { useLayoutEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import type { SiteTransferRedemptionOption } from '@/api/gen'
import { useRedemptionsRedemptionOptionsSiteTransferList } from '@/api/gen'
import { Loader } from '@/components/loader'
import { useAuth } from '@/hooks/use-auth'
import { redemptionListQueryOptions } from '@/pages/rewards/redemption-query-options'

import { RedeemAmountField } from './redeem-amount-field'
import { RedeemOptionCard } from './redeem-option-card'
import { RoomRedeemModal } from './room-redeem-modal'

export const RoomTransferForm = () => {
  const { data, isPending } = useRedemptionsRedemptionOptionsSiteTransferList({
    query: {
      ...redemptionListQueryOptions,
      refetchOnMount: false,
    },
  })
  const { user } = useAuth()

  const [selectedRoom, setSelectedRoom] = useState<SiteTransferRedemptionOption | null>(null)
  const [edgeToRedeem, setEdgeToRedeem] = useState(0)
  const [showModal, setShowModal] = useState(false)

  useLayoutEffect(
    function syncSelectedRoomWithOptions() {
      if (data === undefined || data.length === 0) {
        return
      }
      setSelectedRoom((prev) => {
        if (prev && data.some((r) => r.poker_site_id === prev.poker_site_id)) {
          return prev
        }

        return data[0] ?? null
      })
    },
    [data],
  )

  const onChangeCoin = (coin: SiteTransferRedemptionOption) => {
    if (selectedRoom !== coin) {
      setSelectedRoom(coin)
    }
  }

  const onToggleModal = () => {
    setShowModal((prev) => !prev)
  }

  if (!user) {
    return null
  }

  if (isPending) {
    return <Loader />
  }
  if (!data || data.length === 0) {
    return (
      <Typography
        color="text.secondary"
        variant="body3"
      >
        <FormattedMessage id="edge.premiumRedeem.noRoomOptions" />
      </Typography>
    )
  }
  const edgeBalance = Number.isNaN(Number(user.edge_balance)) ? 0 : Number(user.edge_balance)
  const isAmountInvalid =
    selectedRoom !== null &&
    edgeToRedeem > 0 &&
    (edgeToRedeem < selectedRoom.minimum_redemption_amount || edgeToRedeem > edgeBalance)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {data.map((option) => (
          <RedeemOptionCard
            key={option.poker_site_id}
            wideLogo
            icon={option.poker_room_logo}
            isActive={selectedRoom?.poker_site_id === option.poker_site_id}
            name={option.poker_room_name}
            onClick={() => {
              onChangeCoin(option)
            }}
          />
        ))}
      </div>
      <div className="flex gap-4 sm:flex-col sm:gap-8">
        <RedeemAmountField
          isInvalid={isAmountInvalid}
          value={edgeToRedeem}
          helper={
            edgeToRedeem > edgeBalance ? (
              <FormattedMessage
                id="edge.premiumRedeem.insufficientBalance"
                values={{ balance: edgeBalance }}
              />
            ) : selectedRoom ? (
              <FormattedMessage
                id="edge.premiumRedeem.minimumAmount"
                values={{ amount: selectedRoom.minimum_redemption_amount }}
              />
            ) : (
              ''
            )
          }
          onValueChange={setEdgeToRedeem}
        />
        <Button
          className="flex-[0_0_180px] sm:flex-[1_0_auto]"
          size="large"
          variant="contained"
          disabled={
            data.length === 0 ||
            selectedRoom === null ||
            edgeToRedeem < selectedRoom.minimum_redemption_amount ||
            edgeToRedeem > edgeBalance
          }
          onClick={onToggleModal}
        >
          <FormattedMessage id="common.controls.continue" />
        </Button>
      </div>
      {selectedRoom && (
        <RoomRedeemModal
          edge={edgeToRedeem}
          open={showModal}
          room={selectedRoom}
          onClose={onToggleModal}
        />
      )}
    </div>
  )
}
